import React, { useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronDown } from 'lucide-react';
import { userService } from '../_services';
import { cn } from '@/lib/utils';

function CompliancePopup({ text, sub, closePopup, open: openProp = true }) {
    const [loading, setLoading] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [error, setError] = useState('');
    const [openCards, setOpenCards] = useState(new Set());
    const scrollContainerRef = useRef(null);
    
    const userdata = JSON.parse(localStorage.getItem('user'));

    const handleCardClick = useCallback((index) => {
        setOpenCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
        
        // Check scroll position after accordion animation completes
        setTimeout(() => {
            if (scrollContainerRef.current) {
                const scrollBottom = scrollContainerRef.current.scrollHeight - scrollContainerRef.current.scrollTop;
                const clientHeight = scrollContainerRef.current.clientHeight;
                const isAtBottom = scrollBottom <= clientHeight + 5;
                
                if (isAtBottom) {
                    setIsButtonDisabled(false);
                }
            }
        }, 100);
    }, []);

    const handleScroll = useCallback((e) => {
        try {
            // Use a threshold of 5px to account for rounding and dynamic content changes
            const scrollBottom = e.target.scrollHeight - e.target.scrollTop;
            const clientHeight = e.target.clientHeight;
            const isAtBottom = scrollBottom <= clientHeight + 5;
            
            if (isAtBottom) {
                setIsButtonDisabled(false);
            }
        } catch (error) {
        }
    }, []);

    const handleAcceptCompliance = useCallback(async () => {
        setLoading(true);
        setError('');
        
        try {
            // Update localStorage
            const userDataChange = { ...userdata, iscompopup: true };
            localStorage.setItem('user', JSON.stringify(userDataChange));
            
            // Call API
            const apidata = await userService.Accept_Comp_Popup(
                parseInt(userdata.wholesaler_Id), 
                0, 
                userdata.userrole, 
                'submit'
            );
            
            if (apidata && apidata.length > 0 && apidata[0].isAccepted === true) {
                closePopup();
            } else {
                setError('Something went wrong. Please try later.');
            }
        } catch (error) {
            setError('Something went wrong. Please try later.');
        } finally {
            setLoading(false);
        }
    }, [userdata, closePopup]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closePopup();
        }
    }, [closePopup]);

    const faqItems = [
        {
            question: "Can I offer a doctor, pharmacist or other health care worker or pharmacy employee money or gifts to promote the GlicRx Program?",
            answer: "No! Absolutely not."
        },
        {
            question: "Can I get a doctor, pharmacist or other health care worker or pharmacy employee to become an affiliate of the GlicRx Program?",
            answer: "No! No doctor, nurse, pharmacist, pharmacy intern or tech, or any other health care employee can serve as a marketer of the GlicRx Program."
        },
        {
            question: "What if a doctor, pharmacist, healthcare worker or pharmacy employee asks me for payment in exchange for promoting the GlicRx Program?",
            answer: (
                <div>
                    <p>Politely decline and make it clear that such arrangements are against company policy.</p>
                    <p className="mt-2">You should report such requests to <a href="mailto:compliance@glichealth.com" className="text-primary hover:underline">compliance@glichealth.com</a>. If you would prefer to make a confidential, anonymous report, you can use one of these reporting options:</p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                        <li>Website: <a href="https://www.lighthouse-services.com/_StandardCustomURL/LHILandingPage.asp" target="_blank" rel="noreferrer" className="text-primary hover:underline">www.lighthouse-services.com/glichealth</a></li>
                        <li>Toll-Free Telephone:
                            <ul className="list-circle ml-6 mt-1">
                                <li>English speaking: <strong>833-401-0002</strong></li>
                                <li>Spanish speaking: <strong>800-216-1288</strong></li>
                            </ul>
                        </li>
                    </ul>
                </div>
            )
        },
        {
            question: "Do I have to get your approval of my web site or social media posts?",
            answer: "Yes! In order for us to ensure our marketing network complies with all applicable federal, state and local regulations, we require every marketer – and every contractor of every marketer – to submit their marketing materials – which includes, for example, your web site, social media posts, and marketing emails. Failure to do so will subject your contract to immediate termination."
        },
        {
            question: "What if I forget to get pre-approval of my social media or other online posts?",
            answer: "You must immediately delete any such posts (whether inadvertent or not) and submit such inappropriately posted marketing material to Glic Health for review and approval."
        },
        {
            question: "What if one of my subcontractors uses marketing material that they did not submit for pre-approval?",
            answer: "If you learn that one of your contractors has failed to secure pre-approval of their marketing material, you must immediately (1) cause that contractor to cease immediately the use of any such material and (2) report the same to Glic Health – together with the unapproved marketing materials – for review. Any marketer – and any subcontractor of any marketer – is subject to immediate termination for failure to have your marketing material pre-approved by Glic Health."
        },
        {
            question: "What if a pharmacist or other pharmacy employee asks me or a member of my team for a referral fee or some other payment or gift in connection with the GlicRx Program?",
            answer: (
                <div>
                    <p>You must report any such request – or even the suggestion of such a request – immediately to <a href="mailto:compliance@glichealth.com" className="text-primary hover:underline">compliance@glichealth.com</a>. If you would prefer to make a confidential, anonymous report, you can use one of these reporting options:</p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                        <li>Website: <a href="https://www.lighthouse-services.com/_StandardCustomURL/LHILandingPage.asp" target="_blank" rel="noreferrer" className="text-primary hover:underline">www.lighthouse-services.com/glichealth</a></li>
                        <li>Toll-Free Telephone:
                            <ul className="list-circle ml-6 mt-1">
                                <li>English speaking: <strong>833-401-0002</strong></li>
                                <li>Spanish speaking: <strong>800-216-1288</strong></li>
                            </ul>
                        </li>
                    </ul>
                </div>
            )
        },
        {
            question: "What if I have concerns that someone is out there distributing the GlicRx Program in violation of GlicRx compliance standards?",
            answer: (
                <div>
                    <p>If you have any such concerns, please contact <a href="mailto:compliance@glichealth.com" className="text-primary hover:underline">compliance@glichealth.com</a> immediately so that we can address the situation as soon as possible. If you would prefer to make a confidential, anonymous report, you can use one of these reporting options:</p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                        <li>Website: <a href="https://www.lighthouse-services.com/_StandardCustomURL/LHILandingPage.asp" target="_blank" rel="noreferrer" className="text-primary hover:underline">www.lighthouse-services.com/glichealth</a></li>
                        <li>Toll-Free Telephone:
                            <ul className="list-circle ml-6 mt-1">
                                <li>English speaking: <strong>833-401-0002</strong></li>
                                <li>Spanish speaking: <strong>800-216-1288</strong></li>
                            </ul>
                        </li>
                    </ul>
                </div>
            )
        },
        {
            question: "What if I have questions about compliance or about what marketing activities I can engage in?",
            answer: (
                <p>If you have any such questions, please contact your customer success manager, who can direct your questions to the appropriate GlicRx professional. Of course, you can always reach out to <a href="mailto:compliance@glichealth.com" className="text-primary hover:underline">compliance@glichealth.com</a> as well.</p>
            )
        }
    ];

    return (
        <Dialog open={openProp} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-2xl">{text}</DialogTitle>
                    {sub && <p className="text-sm text-muted-foreground mt-2">{sub}</p>}
                </DialogHeader>
                
                <div 
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto pr-2 space-y-2"
                >
                    {faqItems.map((item, index) => (
                        <div key={index} className={cn(
                            "border rounded-lg transition-colors",
                            openCards.has(index) && "bg-accent"
                        )}>
                            <Button
                                variant="ghost"
                                onClick={() => handleCardClick(index)}
                                className="w-full flex items-start justify-between gap-4 p-4 text-left h-auto whitespace-normal hover:bg-accent/50"
                            >
                                <span className="font-medium flex-1">{item.question}</span>
                                <ChevronDown className={cn(
                                    "h-5 w-5 shrink-0 transition-transform",
                                    openCards.has(index) && "transform rotate-180"
                                )} />
                            </Button>
                            {openCards.has(index) && (
                                <div className="px-4 pb-4 text-sm text-muted-foreground">
                                    {typeof item.answer === 'string' ? <p>{item.answer}</p> : item.answer}
                                </div>
                            )}
                        </div>
                    ))}
                    
                    <div className="pt-4 border-t">
                        <p className="text-sm font-medium">Need Further Assistance?</p>
                        <a href="mailto:info@glichealth.com" className="text-sm text-primary hover:underline">
                            Info@glichealth.com
                        </a>
                    </div>
                </div>

                <div className="pt-4 border-t space-y-2">
                    <Button 
                        onClick={handleAcceptCompliance} 
                        disabled={isButtonDisabled || loading}
                        className="w-full"
                    >
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        I Understand
                    </Button>
                    {error && (
                        <div className="text-sm text-destructive text-center">{error}</div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default CompliancePopup;
